<script lang="ts">
  import ProblemNotice from './lib/ProblemNotice.svelte'
  import { appProblem } from './lib/problems'
  import { onMount } from 'svelte'
  import Brand from './lib/Brand.svelte'
  import TitleBar from './lib/TitleBar.svelte'
  import LiveBar from './lib/LiveBar.svelte'
  import { watchLive } from './lib/live.svelte'
  import { t } from './lib/i18n.svelte'
  import { applyAppearance, effectiveAppearance, toggleAppearance } from './lib/prefs.svelte'
  import Icon from './lib/Icon.svelte'
  // Restores a saved login on every page and finishes a Google/Discord round trip.
  // Signed in, that also opens the friends connection; signed out, it never opens.
  import './lib/account.svelte'
  import { applyDevQuery, dev } from './lib/dev.svelte'
  import { router, type Route } from './lib/router.svelte'
  import { isDesktopApp, isMobileBuild } from './lib/platform'
  import { installNow, update } from './lib/update.svelte'
  import Home from './routes/Home.svelte'
  import Host from './routes/Host.svelte'
  import Viewer from './routes/Viewer.svelte'
  import Debug from './routes/Debug.svelte'
  import Settings from './routes/Settings.svelte'
  import Friends from './routes/Friends.svelte'
  import Mini from './routes/Mini.svelte'
  import Overlay from './routes/Overlay.svelte'
  import Marks from './routes/Marks.svelte'
  import ProfileSetup from './lib/ProfileSetup.svelte'
  import { friends } from './lib/friends.svelte'
  import NotFound from './routes/NotFound.svelte'

  const route = $derived(router.current)
  // Reads prefs.appearance, so the icon and label follow every change, including via Settings.
  const isDark = $derived(effectiveAppearance() === 'dark')
  const switchLabel = $derived(isDark ? t('Switch to light mode') : t('Switch to dark mode'))
  let hostMounted = $state(false)
  $effect(() => {
    if (route.name === 'host' && !isMobileBuild) hostMounted = true
  })
  // The desktop app draws its own header on each screen.
  const inDesktopApp = isDesktopApp

  applyDevQuery()
  onMount(applyAppearance)
  onMount(watchLive)

  const friendName = () => new URLSearchParams(location.search).get('n') ?? ''

  // Type predicates rather than inline `route.name === ...` checks: they narrow
  // reliably through Svelte's generated template types, which is what lets
  // `<Viewer code={route.code} />` type-check.
  const isViewer = (r: Route): r is Extract<Route, { name: 'viewer' }> => r.name === 'viewer'
  const isMissing = (r: Route): r is Extract<Route, { name: 'notFound' }> => r.name === 'notFound'
</script>

{#if inDesktopApp && route.name !== 'mini' && route.name !== 'overlay' && route.name !== 'marks'}
  <TitleBar />
{:else if !inDesktopApp && route.name !== 'viewer'}
  <header class="topbar">
    <a class="brand" href="/" aria-label="sofa home"><Brand wordmark={false} size={24} /></a>
    <nav class="row">
      {#if dev.on && !isMobileBuild}
        <a class="faint" href="/debug">{t('Protocol debug')}</a>
      {/if}
      <button
        type="button"
        class="theme-toggle"
        title={switchLabel}
        aria-label={switchLabel}
        onclick={toggleAppearance}
      >
        <Icon name={isDark ? 'sun' : 'moon'} size={18} />
      </button>
    </nav>
  </header>
{/if}

{#if inDesktopApp && route.name !== 'mini' && route.name !== 'overlay' && route.name !== 'marks' && update.available}
  <div class="notice notice--ok row" style="justify-content: space-between; margin: 8px 16px 0">
    <span>{t('Sofa {version} is ready.', { version: update.available.version })}</span>
    <button type="button" class="primary" disabled={update.installing} onclick={installNow}>
      {update.installing ? t('Updating…') : t('Update and restart')}
    </button>
  </div>
  {#if update.error}<div style="margin: 8px 16px 0">
      <ProblemNotice problem={appProblem(update.error, 'update')} compact />
    </div>{/if}
{/if}

{#if inDesktopApp && !['host', 'mini', 'overlay', 'marks', 'viewer'].includes(route.name)}
  <LiveBar />
{/if}

<!-- First sign-in: choose a handle. Rooms and the mini window are never interrupted. -->
{#if friends.needsHandle && route.name !== 'viewer' && route.name !== 'mini' && route.name !== 'overlay' && route.name !== 'marks'}
  <ProfileSetup />
{/if}

<div class="outlet">
  {#if hostMounted}
    <div class="host-outlet" class:host-outlet--hidden={route.name !== 'host'}>
      <Host active={route.name === 'host'} />
    </div>
  {/if}
  {#if isMobileBuild && !['home', 'viewer', 'settings'].includes(route.name)}
    <Home />
  {:else if route.name === 'home'}
    <Home />
  {:else if route.name === 'host'}
    <!-- The host stays mounted so returning does not restore the native session again. -->
  {:else if isViewer(route)}
    <!--
    Keyed on server and room so navigating straight from one room link to another
    destroys and recreates the page. That is what guarantees the old WebSocket
    is closed and no stale "joined" state carries over to the new room.
  -->
    {#key `${route.target.signalUrl}|${route.code}`}
      <Viewer code={route.code} target={route.target} />
    {/key}
  {:else if route.name === 'mini'}
    <Mini />
  {:else if route.name === 'overlay'}
    <Overlay />
  {:else if route.name === 'marks'}
    <Marks />
  {:else if route.name === 'settings'}
    <Settings />
  {:else if route.name === 'friends'}
    <Friends addId={route.addId} addName={friendName()} />
  {:else if route.name === 'debug' && dev.on}
    <Debug />
  {:else if isMissing(route)}
    <NotFound path={route.path} />
  {:else if route.name === 'debug'}
    <NotFound path="/debug" />
  {/if}
</div>

<style>
  .host-outlet {
    display: flex;
    flex: 1;
    min-height: 0;
  }
  .host-outlet--hidden {
    display: none;
  }
</style>
